import { useState, useEffect } from 'react';

import useAuthenticatedAPIServiceCall from '@frolight/hooks/useAuthenticatedAPIServiceCall';
import * as gatewayService from '@frolight/services/api/gatewayService';

const useConnectedPeripheralsData = (gatewayId, unitDeviceId) => {

  const [ loading, setLoading ] = useState(true);
  const [ data, setData ] = useState(null);
  const [ error, setError ] = useState(null);

  const authenticatedAPIServiceCall = useAuthenticatedAPIServiceCall();

  useEffect(() => {
    let mounted = true;

    const getConnectedPeripheralsAbortController = new AbortController();
    const getConnectedPeripherals = async () => {
      if (mounted) {
        setLoading(true);
        setError(null);
      }

      try {
        const connectedPeripherals = await authenticatedAPIServiceCall(
          gatewayService.getPeripheralsConnectedToUnitAndGateway,
          {
            abortController: getConnectedPeripheralsAbortController,
            opts: {
              gatewayId: gatewayId,
              unitDeviceId: unitDeviceId
            }
          }
        );
        
        if (connectedPeripherals && connectedPeripherals.data) {
          // Sort: active peripherals first, then by label
          const connectedPeripheralsWithLabel = connectedPeripherals.data.map(p => {
            return {
              ...p,
              label: p.label ?? p.deviceId
            };
          });
          const sortedPeripherals = connectedPeripheralsWithLabel.sort((a, b) => {
            if (a.status === 'active' && b.status === 'inactive') {
              return -1;
            } else if (a.status === 'inactive' && b.status === 'active') {
              return 1;
            }
            return a.label.localeCompare(b.label);
          })
          if (mounted) setData(sortedPeripherals);
        } else {
          if (mounted) setError(new Error(`Error getting connected peripherals for gateway "${gatewayId}" and unit "${unitDeviceId}".`));
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          if (mounted) setError(err);
        }
      } finally {
        if (mounted) setLoading(false);
      }
    };

    if (gatewayId && unitDeviceId) {
      getConnectedPeripherals();
    }

    return () => {
      mounted = false;
      getConnectedPeripheralsAbortController.abort();
    }

  }, [ gatewayId, unitDeviceId ]);

  return { loading, error, data };
};

export default useConnectedPeripheralsData;
